import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dropdown from '../../../common/components/Dropdown';
import Menu from '../../../common/components/Menu';
import Button from '../../Button';
import useForm from '../../../utils/useForm';
import ConfirmAction from '../../dialogs/ConfirmAction';
import { notifySuccess, notifyError } from '../../../utils/notify';

const PatientEngagementNotification = ({
  recipient,
  patientNotificationsDataUpdate,
  sendSignUpLink,
}) => {
  const { t } = useTranslation();
  const phoneInputRef = useRef();
  const { pepUserName, status, unsubscribedAll } = recipient;
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const handleOnEdit = () => setIsEditing(true);
  const handleOnCancel = () => setIsEditing(false);
  const phone = recipient.getPhoneNumberMobile();
  const email = recipient.getEmailPersonal();
  const { handleSubmit, renderField } = useForm({
    inputs: {
      phone: {
        ref: phoneInputRef,
        label: t('forms:phone.label'),
        type: 'text',
        defaultValue: phone ? phone.number : '',
        showValueOnly: !isEditing,
        disabled: true,
        schema: {
          type: 'string',
          format: 'phone',
          optional: true,
        },
      },
      email: {
        label: t('forms:email.label'),
        type: 'text',
        defaultValue: email ? email.address : '',
        showValueOnly: !isEditing,
        disabled: isSaving,
        schema: {
          type: 'string',
          optional: true,
        },
      },
    },
    onSubmit: ({ email }) => {
      setIsSaving(true);
      patientNotificationsDataUpdate({ email }).then(() => {
        setIsSaving(false);
        handleOnCancel();
        notifySuccess(
          t('confirmations:patientContactDetailsUpdated.success'),
        )();
      });
    },
  });

  const [sendVia, setSendVia] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const handleOnOpenDialog = () => setIsVisible(true);
  const handleOnCloseDialog = () => setIsVisible(false);
  const handleOnOpenSendPEPLinkViaEmail = () => {
    setSendVia('EMAIL');
    handleOnOpenDialog();
  };
  const handleOnOpenSendPEPLinkViaSMS = () => {
    setSendVia('SMS');
    handleOnOpenDialog();
  };
  const handleOnOpenSendPEPLinkViaBoth = () => {
    setSendVia('BOTH');
    handleOnOpenDialog();
  };

  const handleOnSendPEPLink = () => {
    setIsSending(true);
    sendSignUpLink(sendVia.toLowerCase())
      .then((response) => {
        if (
          response &&
          response.data &&
          response.data.sendSignUpRequest &&
          (response.data.sendSignUpRequest.email ||
            response.data.sendSignUpRequest.sms)
        ) {
          notifySuccess(t('confirmations:sendPEPLink.success', { sendVia }))();
        } else {
          notifyError()(t('confirmations:sendPEPLink.error'));
        }
        setIsSending(false);
        handleOnCloseDialog();
      })
      .catch(() => {
        setIsSending(false);
        handleOnCloseDialog();
        notifyError()(t('confirmations:sendPEPLink.error'));
      });
  };

  return (
    <>
      <div className="stack-6">
        <div className="grid grid-cols-2 gap-4 items-center">
          <div className="stack-0">
            <h2>{t('notifications', { count: 1 })}</h2>
            <h3 className="text-lg font-normal">{t('contactDetails')}</h3>
          </div>
          <div className="cluster-4 justify-end">
            {isEditing ? (
              <div className="cluster-2">
                <Button
                  type="primary"
                  onClick={handleSubmit}
                  loading={isSaving}
                >
                  {t('save')}
                </Button>
                <Button
                  type="ghost"
                  onClick={handleOnCancel}
                  disabled={isSaving}
                >
                  {t('cancel')}
                </Button>
              </div>
            ) : (
              <Button type="ghost" onClick={handleOnEdit}>
                {t('edit')}
              </Button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 items-end">
          {renderField('phone')}
          {renderField('email')}
          <Dropdown
            trigger={['click']}
            disabled={
              (!(phone && phone.number) && !(email && email.address)) ||
              (status === 'Inactive' && !unsubscribedAll) ||
              status === 'Deceased'
            }
            overlay={
              <Menu>
                {email && email.address && (
                  <Menu.Item
                    key="email"
                    onClick={handleOnOpenSendPEPLinkViaEmail}
                  >
                    {t('viaWithType', { type: t('email') })}
                  </Menu.Item>
                )}
                {phone && phone.number && (
                  <Menu.Item key="sms" onClick={handleOnOpenSendPEPLinkViaSMS}>
                    {t('viaWithType', { type: t('sms') })}
                  </Menu.Item>
                )}
                {phone && phone.number && email && email.address && (
                  <Menu.Item
                    key="both"
                    onClick={handleOnOpenSendPEPLinkViaBoth}
                  >
                    {t('viaWithType', { type: t('both') })}
                  </Menu.Item>
                )}
              </Menu>
            }
          >
            <Button disabled={true}>
              {pepUserName ? t('sendPEPLogInLink') : t('sendPEPSignUpLink')}
            </Button>
          </Dropdown>
        </div>
      </div>
      <ConfirmAction
        message={t('confirmations:sendPEPLink.message', {
          type: pepUserName ? t('logIn') : t('signUp'),
          sendVia,
        })}
        open={isVisible}
        onConfirm={handleOnSendPEPLink}
        onCancel={handleOnCloseDialog}
        loading={isSending}
      />
    </>
  );
};

export default PatientEngagementNotification;
